import * as React from "react"
import { ChevronsUpDownIcon } from "lucide-react"

import { cn } from "lib/utils"
import { Button } from "components/ui/button"
import { Checkbox } from "components/ui/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover"

export interface MultiSelectComboboxOption {
  value: string
  label: string
}

export interface MultiSelectComboboxProps {
  options: MultiSelectComboboxOption[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
}

export function MultiSelectCombobox({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  disabled = false,
  className,
  buttonClassName,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false)

  const [search, setSearch] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  const filteredOptions = React.useMemo(() => {
    if (!debouncedSearch) return options
    const q = debouncedSearch.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, debouncedSearch])

  const selectedOptions = options.filter((o) => value.includes(o.value))

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue]
    
    onValueChange?.(newValue)
  }

  const handleClearAll = () => {
    onValueChange?.([])
  }

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return placeholder
    if (selectedOptions.length === 1) return selectedOptions[0].label
    return `${selectedOptions.length} selected`
  }

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              buttonClassName
            )}
            disabled={disabled}
          >
            <span className="truncate">
              {getDisplayText()}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0"
          side="bottom"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command className="flex h-64 flex-col">
            <div className="flex items-center justify-between border-b px-3">
              <CommandInput 
                placeholder={searchPlaceholder} 
                value={search}
                onValueChange={setSearch}
                className="flex-1 border-0 bg-transparent px-0 py-2 text-sm outline-none"
              />
              {selectedOptions.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Clear all
                </Button>
              )}
            </div>
            <CommandList className="flex-1 overflow-y-auto overscroll-contain">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleToggleOption(option.value)}
                    className="cursor-pointer"
                  >
                    <Checkbox
                      checked={value.includes(option.value)}
                      className="mr-2"
                      onCheckedChange={() => handleToggleOption(option.value)}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
